<i18n src="./resources/locales.json"></i18n>
<template>
  <div>
    <v-tour name="userIntroduction" :steps="steps" :callbacks="callbacks"></v-tour>
    <v-tour name="initialTour" :steps="initialTourSteps" :callbacks="initialTourCallBacks">
      <template v-slot="tour">
        <v-step
          v-if="tour.currentStep !== -1"
          :key="tour.currentStep"
          :step="tour.steps[tour.currentStep]"
          :previous-step="tour.previousStep"
          :next-step="tour.nextStep"
          :stop="tour.stop"
          :skip="tour.skip"
          :finish="tour.finish"
          :is-first="tour.isFirst"
          :is-last="tour.isLast"
          :labels="tour.labels"
          class="custom-step"
        >
          <template #content>
            <!-- eslint-disable-next-line vue/no-v-html-->
            <div v-html="$t(tour.steps[tour.currentStep].content)"></div>
          </template>
          <template #actions>
            <div class="pt-4">
              <v-btn v-if="!tour.isLast" class="mr-2" dense small outlined @click="tour.skip">
                <span v-once>{{ $t('buttons.skip') }}</span>
              </v-btn>
              <v-btn v-if="!tour.isFirst" class="mr-2" dense small outlined @click="tour.previousStep">
                <span v-once>{{ $t('buttons.previous') }}</span>
              </v-btn>
              <v-btn v-if="!tour.isLast" dense class="mr-2" small outlined color="primary" @click="tour.nextStep">
                <span v-once>{{ $t('buttons.next') }}</span>
              </v-btn>
              <v-btn v-if="tour.isLast" color="primary" dense small outlined @click="tour.finish">
                <span v-once>{{ $t('buttons.finish') }}</span>
              </v-btn>
            </div>
          </template>
        </v-step>
      </template>
    </v-tour>
    <EcsUserIntroductionDialog :show-dialog="showApplicationTour" :title="$t('welcome')" @clickOutside="closeTour">
      <template>
        {{ $t('welcomeContent') }}
      </template>
      <template #actions>
        <v-btn color="primary" text @click="closeTour">
          <span v-once>{{ $t('close') }}</span>
        </v-btn>
        <v-btn color="primary" text @click="startTour">
          <span v-once>{{ $t('takeTour') }}</span>
        </v-btn>
      </template>
    </EcsUserIntroductionDialog>
    <EcsReleaseNotes :show-dialog="showNewFeatures" @clickOutside="closeNewFeaturesDialog" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import EcsUserIntroductionDialog from '@/components/ecs-user-introduction/EcsUserIntroductionDialog';
import EcsReleaseNotes from '@/components/ecs-user-introduction/EcsReleaseNotes';

export default {
  name: 'EcsUserIntroduction',

  components: { EcsUserIntroductionDialog, EcsReleaseNotes },

  data() {
    return {
      showNewFeatures: false,
      initialTourSteps: [
        {
          target: '#tour-agent-state',
          content: 'tour.agentState'
        },
        {
          target: '#tour-load-allowance',
          content: 'tour.loadAllowance'
        },
        {
          target: '#tour-contacts',
          content: 'tour.contacts',
          before: () => {
            this.$root.$emit('open-directory-panel');
            return Promise.resolve(true);
          },
          params: {
            placement: 'left',
            highlight: true
          }
        },
        {
          target: '#tour-callbacks',
          content: 'tour.callbacks',
          before: () => {
            this.$root.$emit('open-callbacks-panel');
            return Promise.resolve(true);
          },
          params: {
            placement: 'left',
            highlight: true
          }
        },
        {
          target: '#tour-notifications',
          content: 'tour.notifications',
          before: () => {
            this.createWelcomeNotification();
            this.$root.$emit('open-notifications-panel');
            return Promise.resolve(true);
          },
          params: {
            placement: 'left',
            highlight: true
          }
        },
        {
          target: '#tour-agent-profile',
          content: 'tour.agentProfile',
          before: () => {
            this.removeWelcomeNotification();
            this.$root.$emit('close-side-panels');
            return Promise.resolve(true);
          },
          params: {
            placement: 'left'
          }
        },
        {
          target: '#side-bar-dashboards',
          content: 'tour.dashboard',
          before: () => {
            this.pinSideBar();
            if (this.$route.name === 'Dashboards') {
              return Promise.resolve(true);
            }
            return this.$router.push('dashboards').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-calls',
          content: 'tour.telephony',
          before: () => {
            if (this.$route.name === 'Calls') {
              return Promise.resolve(true);
            }

            return this.$router.push('calls').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-chats',
          content: 'tour.chat',
          before: () => {
            if (this.$route.name === 'Chats') {
              return Promise.resolve(true);
            }

            return this.$router.push('chats').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-emails',
          content: 'tour.email',
          before: () => {
            if (this.$route.name === 'Emails') {
              return Promise.resolve(true);
            }

            return this.$router.push('emails').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-sms',
          content: 'tour.sms',
          before: () => {
            if (this.$route.name === 'Sms') {
              return Promise.resolve(true);
            }

            return this.$router.push('sms').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-settings',
          content: 'tour.settings',
          before: () => {
            if (this.$route.name === 'Settings') {
              return Promise.resolve(true);
            }

            return this.$router.push('settings').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '#side-bar-help',
          content: 'tour.help',
          before: () => {
            if (this.$route.name === 'Help') {
              return Promise.resolve(true);
            }

            return this.$router.push('help').then(() => {
              return Promise.resolve(true);
            });
          },
          params: {
            highlight: true,
            enableScrolling: false
          }
        },
        {
          target: '.main-stats-container',
          content: 'tour.stats',
          params: {
            enableScrolling: false
          }
        }
      ],
      initialTourCallBacks: {
        onSkip: this.closeTour,
        onFinish: this.closeTour
      },
      steps: [],
      callbacks: {},
      sideBarWasMaximized: true
    };
  },

  computed: {
    ...mapGetters('AGENT', ['isSupervisor']),
    ...mapGetters('SETTINGS', ['featuresSettings']),
    showApplicationTour() {
      return this.featuresSettings.showApplicationTour;
    }
  },

  watch: {
    showApplicationTour(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.showNewFeatures = false;
      }
    },
    'featuresSettings.latestShowedFeatures'(newValue, oldValue) {
      if (!oldValue && newValue === 'no-features') {
        this.showNewFeatures = true;
      }
    }
  },

  mounted() {
    this.$root.$on('open-release-notes', this.openNewFeaturesDialog);
    this.$root.$on('start-tour', this.startTour);
    this.showNewFeatures =
      !this.showApplicationTour &&
      this.featuresSettings.latestShowedFeatures !== null &&
      this.compareApplicationVersion(this.featuresSettings.latestShowedFeatures, process.env.APPLICATION_VERSION);
  },

  beforeDestroy() {
    this.$root.$off('open-release-notes', this.openNewFeaturesDialog);
    this.$root.$off('start-tour', this.startTour);
  },

  methods: {
    ...mapActions('NOTIFICATIONS', ['addSystemNotification', 'removeWelcomeNotification']),

    ...mapActions('SETTINGS', ['updateShowApplicationTour', 'updateLatestShowedFeatures']),

    startTour() {
      this.updateShowApplicationTour(false);
      this.$tours.initialTour.start();
    },

    compareApplicationVersion(lastShownVersion, currentVersion) {
      const splitLatestShownVersion = lastShownVersion.split('.');
      const splitCurrentShownVersion = currentVersion.split('.');
      if (
        splitLatestShownVersion[0] !== splitCurrentShownVersion[0] ||
        splitLatestShownVersion[1] !== splitCurrentShownVersion[1]
      ) {
        return true;
      }
      return false;
    },

    closeTour() {
      this.unpinSideBar();
      this.removeWelcomeNotification();
      // Update the featureSettings in order to avoid showing the application tour
      // if it was introduced by first time
      this.updateShowApplicationTour(false);
    },

    closeNewFeaturesDialog() {
      this.showNewFeatures = false;
      this.updateLatestShowedFeatures(process.env.APPLICATION_VERSION);
    },

    openNewFeaturesDialog() {
      this.showNewFeatures = true;
    },

    pinSideBar() {
      this.sideBarWasMaximized = this.featuresSettings.pinnedSideBarOnMaximizedMode;
      this.$root.$emit('disable-mini-mode-tour');
    },

    unpinSideBar() {
      if (!this.sideBarWasMaximized && this.isSupervisor && this.$featuresManager.supervisorFeatures.enable) {
        this.$root.$emit('enable-mini-mode-tour');
      }
    },

    createWelcomeNotification() {
      const item = {
        id: 'welcome-notification',
        notificationType: 'welcome.message',
        icon: 'mdi-alert-decagram',
        timeAgo: moment().fromNow(),
        headline: { i18nKey: 'welcome.ecs' },
        message: 'welcome.goodToHaveYou',
        actions: [
          {
            name: 'notification.actions.dismiss',
            action: 'dismissNotification'
          }
        ]
      };
      this.addSystemNotification(item);
    }
  }
};
</script>

<style scoped>
.custom-step {
  background-color: var(--v-surface-lighten1);
  color: inherit;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}
</style>
